'use client';

import clsx from 'clsx';
import Link from 'next/link';
import { Fragment, type CSSProperties } from 'react';
import { Title } from '@publishing/components/ui/Title';
import { CommonInner } from '@publishing/components/ui/CommonInner';
import { Carousel } from './Carousel';
import Badge from '@publishing/components/ui/Badge';
import Icon from '@publishing/components/ui/Icon';
import { EtcHourglass } from '@publishing/components/ui/SvgSprite';
import { getSafeHtmlTextLines } from '@publishing/helpers/format';
import { getPublishingImage } from '@/shared/utils/publishingAsset';
import { GTM_EVENT } from '@/shared/constants/gtm';
import { gtmClick } from '@/shared/utils/gtm';
import { Navigation } from 'swiper';
import { useMediaQuery } from '@publishing/hooks/useMediaQuery';
import s from './InsightCarousel.module.scss';

export interface InsightArticle {
  image?: string;
  category?: string;
  color?: string;
  title: string;
  href: string;
  time?: number;
  date?: string;
  keyword?: string[];
}

interface InsightCarouselProps {
  articles: InsightArticle[];
  theme?: 'primary' | 'secondary';
}

function renderSafeTitle(title: string) {
  const lines = getSafeHtmlTextLines(title);

  return lines.map((line, index) => (
    <Fragment key={`${line}-${index}`}>
      {line}
      {index < lines.length - 1 && <br />}
    </Fragment>
  ));
}

export default function InsightCarousel({
  articles,
  theme = 'primary',
}: InsightCarouselProps) {
  const isPc = useMediaQuery('(min-width: 1024px)', false);

  return (
    <section className={clsx(s.fastfive_insight, s[`fastfive_insight_${theme}`])}>
      <CommonInner className={s.fastfive_insight_inner}>
        <div className={s.fastfive_insight_title_area}>
          <p className={s.fastfive_insight_sub_title}>업무 공간에 대한 깊이 있는 시선</p>
          <Title className={s.fastfive_insight_title}>
            패파 인사이트
          </Title>
        </div>
        <Carousel
          key={String(isPc)}
          className={s.fastfive_insight_carousel}
          gap={10}
          loop={articles.length > 3}
          // speed={500}
          slidesPerView={1}
          modules={[Navigation]}
          breakpoints={{
            1025: {
              spaceBetween: 0,
              slidesPerView: 3,
            },
          }}
        >
          {articles.map((article) => (
            <Link
              href={article.href}
              key={article.href}
              className={s.fastfive_insight_item}
              style={article.color ? { '--post-category-color': `var(--color-${article.color.toLowerCase()}-600)` } as CSSProperties : undefined}
              // btnname 은 글 제목 — 원문에 <br> 등 마크업이 섞여 있어 화면과 동일한 평문으로 맞춘다.
              {...gtmClick(GTM_EVENT.BTN, getSafeHtmlTextLines(article.title).join(' '), article.href)}
            >
              <div
                className={s.fastfive_insight_img_area}
                style={
                  article.image
                    ? undefined
                    : { backgroundImage: `url(${getPublishingImage('common/img_blog_thumbnail.jpg')})` }
                }
              >
                {article.image ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={article.image} alt={article.title} className={s.fastfive_insight_img} />
                ) : null}
              </div>
              <div className={s.fastfive_insight_info_area}>
                {article.category ? <span className={s.fastfive_insight_category}>{article.category}</span> : null}
                <strong className={s.fastfive_insight_item_title}>{renderSafeTitle(article.title)}</strong>
                {(article.time || article.date) && (
                  <div className={s.fastfive_insight_time_area}>
                    {article.time && (
                      <span className={s.fastfive_insight_time}>
                        <Icon name={EtcHourglass} theme="line" size={20} className={s.fastfive_insight_time_icon} />
                        {article.time}분
                      </span>
                    )}
                    {article.date && <span className={s.fastfive_insight_date}>{article.date}</span>}
                  </div>
                )}
                {article.keyword && (
                  <div className={s.fastfive_insight_keyword_area}>
                    {article.keyword.map((keyword) => (
                      <Badge key={keyword} className={s.fastfive_insight_keyword}>{keyword}</Badge>
                    ))}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </Carousel>
      </CommonInner>
    </section>
  );
}
